<template>
  <div class="stat-card" :class="[`card-${type}`, { 'loading': loading }]">
    <div class="card-content">
      <div class="card-header">
        <div class="card-icon" :style="{ backgroundColor: iconBgColor }">
          <el-icon :color="iconColor" size="24">
            <component :is="icon" />
          </el-icon>
        </div>
        <div class="card-meta">
          <h3 class="card-title">{{ title }}</h3>
          <p class="card-subtitle">{{ subtitle }}</p>
        </div>
      </div>
      
      <div class="card-body">
        <div class="main-value">
          <span v-if="prefix" class="value-prefix">{{ prefix }}</span>
          <span class="value-number">
            <animated-number 
              v-if="!loading"
              :value="value" 
              :duration="animationDuration"
              :format="numberFormat"
            />
            <el-skeleton-item v-else variant="text" style="width: 60px; height: 32px;" />
          </span>
          <span v-if="suffix" class="value-suffix">{{ suffix }}</span>
        </div>
        
        <div v-if="change !== undefined" class="change-indicator">
          <el-icon 
            :class="changeClass" 
            size="14"
          >
            <ArrowUp v-if="change > 0" />
            <ArrowDown v-if="change < 0" />
            <Minus v-if="change === 0" />
          </el-icon>
          <span :class="changeClass">
            {{ Math.abs(change) }}{{ changeUnit }}
          </span>
          <span class="change-text">{{ changePeriod }}</span>
        </div>
        
        <div v-if="extraInfo" class="extra-info">
          {{ extraInfo }}
        </div>
      </div>
    </div>
    
    <div v-if="showChart && chartData" class="card-chart">
      <mini-chart 
        :type="chartType"
        :data="chartData"
        :color="chartColor"
        :height="60"
      />
    </div>
    
    <div v-if="actions && actions.length > 0" class="card-actions">
      <el-button
        v-for="action in actions"
        :key="action.key"
        :type="action.type || 'text'"
        :size="action.size || 'small'"
        @click="$emit('action', action.key)"
      >
        {{ action.label }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { ArrowUp, ArrowDown, Minus } from '@element-plus/icons-vue';
import AnimatedNumber from './AnimatedNumber.vue';
import MiniChart from './MiniChart.vue';

export default {
  name: 'StatCard',
  components: {
    ArrowUp,
    ArrowDown, 
    Minus,
    AnimatedNumber,
    MiniChart
  },
  props: {
    // 基础信息
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    value: {
      type: [Number, String],
      required: true
    },
    prefix: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      default: ''
    },
    
    // 图标相关
    icon: {
      type: [String, Object],
      required: true
    },
    iconColor: {
      type: String,
      default: '#ffffff'
    },
    iconBgColor: {
      type: String,
      default: '#409eff'
    },
    
    // 样式类型
    type: {
      type: String,
      default: 'default',
      validator: value => ['default', 'primary', 'success', 'warning', 'danger'].includes(value)
    },
    
    // 变化指示
    change: {
      type: Number,
      default: undefined
    },
    changeUnit: {
      type: String,
      default: '%'
    },
    changePeriod: {
      type: String,
      default: '较昨日'
    },
    
    // 额外信息
    extraInfo: {
      type: String,
      default: ''
    },
    
    // 图表相关
    showChart: {
      type: Boolean,
      default: false
    },
    chartType: {
      type: String,
      default: 'line',
      validator: value => ['line', 'bar', 'area'].includes(value)
    },
    chartData: {
      type: Array,
      default: () => []
    },
    chartColor: {
      type: String,
      default: '#409eff'
    },
    
    // 操作按钮
    actions: {
      type: Array,
      default: () => []
    },
    
    // 状态
    loading: {
      type: Boolean,
      default: false
    },
    
    // 动画
    animationDuration: {
      type: Number,
      default: 2000
    },
    numberFormat: {
      type: Function,
      default: (value) => value.toLocaleString()
    }
  },
  emits: ['action'],
  setup(props) {
    const changeClass = computed(() => {
      if (props.change === undefined) return '';
      if (props.change > 0) return 'change-positive';
      if (props.change < 0) return 'change-negative';
      return 'change-neutral';
    });
    
    return {
      changeClass
    };
  }
};
</script>

<style scoped>
.stat-card {
  background: var(--surface-bg);
  border-radius: 14px;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.stat-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.stat-card.loading {
  pointer-events: none;
}

.card-content {
  padding: 24px;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-meta {
  flex: 1;
  min-width: 0;
}

.card-title {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
  line-height: 1.4;
}

.card-subtitle {
  margin: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.4;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.main-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.value-prefix,
.value-suffix {
  font-size: 18px;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.value-number {
  font-size: 32px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1;
}

.change-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.change-positive {
  color: var(--color-success);
}

.change-negative {
  color: var(--color-danger);
}

.change-neutral {
  color: var(--color-text-secondary);
}

.change-text {
  color: #999;
}

.extra-info {
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.4;
}

.card-chart {
  padding: 0 24px 16px;
}

.card-actions {
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* 卡片类型样式 */
.card-primary .card-icon {
  background: linear-gradient(135deg, #2F6BFF, #6FA8FF);
}

.card-success .card-icon {
  background: linear-gradient(135deg, #22C55E, #86EFAC);
}

.card-warning .card-icon {
  background: linear-gradient(135deg, #F59E0B, #FDE68A);
}

.card-danger .card-icon {
  background: linear-gradient(135deg, #EF4444, #FCA5A5);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .card-content {
    padding: 16px;
  }
  
  .card-header {
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .card-icon {
    width: 40px;
    height: 40px;
  }
  
  .value-number {
    font-size: 24px;
  }
  
  .card-chart {
    padding: 0 16px 12px;
  }
  
  .card-actions {
    padding: 12px 16px;
    flex-direction: column;
  }
}

/* 加载状态 */
.loading .card-content {
  opacity: 0.7;
}

/* 动画效果 */
.stat-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style> 
